/**
 * 表のセルが持つ「他質問への UUID 参照」の張り替え。
 * 固定行セルは列の複製だが、参照先は列ではなく同じ行の兄弟セルでなければならない。
 */

import { TemplateQuestion } from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'

/**
 * 質問が持つ「他質問への UUID 参照」を uuidMap に従って張り替える。
 * 対象は計算式トークン(questionNodeUUID)と時間計測の開始/終了(start/endQuestionNodeUUID)。
 * map に無い uuid はそのまま残す(参照未設定・表外参照など)。
 */
export const remapSiblingQuestionRefs = (
  question: TemplateQuestion,
  uuidMap: ReadonlyMap<string, string>,
): TemplateQuestion => {
  const remap = (uuid: string): string => uuidMap.get(uuid) ?? uuid

  // 元の質問定義に無いキーを undefined として増やさない。
  return {
    ...question,
    ...(question.responseFormulas && {
      responseFormulas: question.responseFormulas.map(formula => ({
        ...formula,
        tokens: formula.tokens?.map(token =>
          token.questionNodeUUID === undefined
            ? token
            : { ...token, questionNodeUUID: remap(token.questionNodeUUID) },
        ),
      })),
    }),
    ...(question.responseTimeMeasurements && {
      responseTimeMeasurements: question.responseTimeMeasurements.map(
        measurement => ({
          ...measurement,
          startQuestionNodeUUID: remap(measurement.startQuestionNodeUUID),
          endQuestionNodeUUID: remap(measurement.endQuestionNodeUUID),
        }),
      ),
    }),
  }
}

/**
 * 複製したサブツリーの全質問ノードについて、参照(計算式・時間計測)を uuidMap で張り替える。
 * 行の全セルの uuid 対応が揃ってからでないと張り替えられないため、複製後にまとめて適用する。
 */
export const remapSubtreeQuestionRefs = (
  nodes: TemplateNodesDict,
  uuidMap: ReadonlyMap<string, string>,
): TemplateNodesDict =>
  Object.fromEntries(
    Object.entries(nodes).map(([id, node]) => [
      Number(id),
      node.question
        ? {
            ...node,
            question: remapSiblingQuestionRefs(node.question, uuidMap),
          }
        : node,
    ]),
  )
